/**
 * The anonymous onboarding session's client lifecycle: mint the session
 * cookie once, retry a failed mint, and detach it on "Start over" (ADR 041).
 *
 * Invariants:
 * - Every mint in flight is the one tracked promise. An untracked mint escapes
 *   detachSession's wait and can Set-Cookie after the DELETE, binding the
 *   browser to a session nobody detached.
 * - mintSessionOnce is deliberately not `async`: it returns the tracked
 *   promise object itself, and callers compare promises with `===`.
 * - Only the caller whose attempt failed re-arms the tracked promise, and it
 *   retries in the same continuation, so concurrent callers and
 *   detachSession see the retry instead of starting their own.
 * - A mint always settles to a boolean and never rejects; mintOnboardingSession
 *   bounds it with SESSION_MINT_TIMEOUT_MS.
 * - The mint is forgotten only by a successful detach. The next conversation
 *   MUST mint again: only a CREATE response carries the claim token the
 *   sign-up handoff needs. A failed detach keeps it, because the browser may
 *   still hold the old cookie.
 * - A successful detach clears the tracked promise unconditionally. While the
 *   DELETE is in flight, mintSessionOnce returns the settled tracked promise,
 *   but ensureSession's re-arm after a failed mint starts a new one that the
 *   detach neither awaits nor tracks: callers MUST NOT run ensureSession
 *   during a detach.
 */
"use client";

import { useCallback, useEffect, useRef } from "react";
import { currentPosthogDistinctId } from "@/lib/analytics/browserDistinctId";
import { storeStashedClaimToken } from "@/lib/onboarding/handoff";
import { detachOnboardingSession, mintOnboardingSession } from "@/lib/onboarding/session";
import { captureClientError } from "@/lib/sentry/capture-client";

export function useOnboardingSessionLifecycle(): {
  mintSessionOnce: () => Promise<boolean>;
  ensureSession: () => Promise<boolean>;
  detachSession: () => Promise<boolean>;
} {
  // null = no mint since mount or since the last successful detach.
  const mintPromiseRef = useRef<Promise<boolean> | null>(null);
  const mintAbortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Re-armed on setup, not only cleared on cleanup: Strict Mode's dev
    // mount, unmount, remount replay would otherwise leave it false forever.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      mintAbortRef.current?.abort();
    };
  }, []);

  // Deferred to a microtask so the mint never races ahead of a same-turn
  // website parse request.
  const mintSessionOnce = useCallback((): Promise<boolean> => {
    if (mintPromiseRef.current) return mintPromiseRef.current;
    const promise = new Promise<boolean>((resolve) => {
      queueMicrotask(() => {
        if (!isMountedRef.current) {
          resolve(false);
          return;
        }
        const controller = new AbortController();
        mintAbortRef.current = controller;
        void (async () => {
          try {
            const mint = await mintOnboardingSession({
              signal: controller.signal,
              posthogDistinctId: currentPosthogDistinctId(),
            });
            if (mint.usable && mint.claimToken) storeStashedClaimToken(mint.claimToken);
            resolve(mint.usable);
          } catch (err) {
            // mintOnboardingSession never rejects, so a throw here is a bug
            // (a missing mock export, a broken import). The promise must still
            // settle, or every caller awaiting it hangs.
            captureClientError(err, {
              feature: "onboarding-chat",
              errorType: "session-mint-threw",
              impact: "degraded",
            });
            resolve(false);
          } finally {
            if (mintAbortRef.current === controller) mintAbortRef.current = null;
          }
        })();
      });
    });
    mintPromiseRef.current = promise;
    return promise;
  }, []);

  // Guarantees a usable session cookie before a cookie-authenticated request
  // (the deck-parse POST 401s without it, LEV-1293), retrying a failed mint
  // once so a rate-limited first-turn mint does not doom the upload.
  const ensureSession = useCallback(async (): Promise<boolean> => {
    const attempt = mintSessionOnce();
    if (await attempt) return true;
    if (mintPromiseRef.current === attempt) mintPromiseRef.current = null;
    return mintSessionOnce();
  }, [mintSessionOnce]);

  // Loops until no new mint replaced the awaited one: ensureSession's retry
  // starts in the continuation of the mint it replaces.
  const detachSession = useCallback(async (): Promise<boolean> => {
    let pendingMint = mintPromiseRef.current;
    while (pendingMint) {
      await pendingMint;
      if (mintPromiseRef.current === pendingMint) break;
      pendingMint = mintPromiseRef.current;
    }
    const detached = await detachOnboardingSession();
    if (detached) mintPromiseRef.current = null;
    return detached;
  }, []);

  return { mintSessionOnce, ensureSession, detachSession };
}
